import isEqual from 'hs-lodash/isEqual';

import {
  combineFilters,
  combineFilterStates,
  getInitialFiltersStateForUIMode,
  makeFiltersState,
} from 'framework-listing-lib/utils/filters';
import {
  FiltersState,
  FilterStateSource,
  FrameworkListingLibProviderProps,
  UIMode,
  View,
} from 'framework-listing-lib/types';

import { createStore, Store } from './zustand';
import { INITIAL_FILTER_STATE } from '../../constants/filters';

export interface FilterStoreState {
  filtersState: FiltersState;
  /**
   * Flag used to determine whether a filters change came from the filter editor, or some other
   * source. Changes to the filters from outside of the filter editor should cause the editor to remount
   * to clear its internal cache of the filter state
   */
  filterStateSource: FilterStateSource;
  deletedObjects: number[];
  hasUsedQueryParams: boolean;
  shouldSyncWithSessionStorage: boolean;
}

export interface FilterStoreActions {
  setQuery: (query: string) => void;
  setSort: (sort: FiltersState['sort']) => void;
  setPagination: (pagination: FiltersState['pagination']) => void;
  resetPaginationOffset: () => void;
  setDeletedObjects: (
    update: number[] | ((prev: number[]) => number[])
  ) => void;

  initializeFiltersState: (p: {
    currentView: View | undefined;
    uiMode: UIMode;
    initialFilter: FrameworkListingLibProviderProps['initialFilter'];
  }) => void;
  updateInitialFilters: (p: {
    initialFilter: FrameworkListingLibProviderProps['initialFilter'];
    uiMode: UIMode;
  }) => void;
  consumeQueryParams: (p: {
    urlParams: {
      filterGroups: FiltersState['filterGroups'];
      // legacy filters from before OR groups
      filters: FiltersState['quickFilters'];
      quickFilters: FiltersState['quickFilters'];
    };
    initialFilter: FrameworkListingLibProviderProps['initialFilter'];
    currentView: View | undefined;
    uiMode: UIMode;
  }) => void;

  setFilters: (
    update: FiltersState | ((prev: FiltersState) => FiltersState),
    options?: { source: FilterStateSource }
  ) => void;
}

export type FilterSlice = FilterStoreState & FilterStoreActions;

export type FilterStore = Store<FilterSlice>;

function resolveFiltersUpdate(
  update: FiltersState | ((prev: FiltersState) => FiltersState),
  prev: FiltersState
): FiltersState {
  return typeof update === 'function' ? update(prev) : update;
}

export function createFilterStore(): FilterStore {
  return createStore<FilterSlice>(
    (set, __get, __api) => ({
      filtersState: INITIAL_FILTER_STATE,
      filterStateSource: 'other' as FilterStateSource,
      deletedObjects: [],
      hasUsedQueryParams: false,
      shouldSyncWithSessionStorage: false,

      setQuery: (query) =>
        set((state) => {
          state.filtersState.query = query;
          state.filtersState.pagination.offset = 0;
        }),
      setSort: (sort) =>
        set((state) => {
          state.filtersState.sort = sort;
          state.filtersState.pagination.offset = 0;
        }),
      setPagination: (pagination) =>
        set((state) => {
          state.filtersState.pagination = pagination;
        }),
      resetPaginationOffset: () =>
        set((state) => {
          state.filtersState.pagination.offset = 0;
        }),
      setDeletedObjects: (update) =>
        set((state) => {
          state.deletedObjects =
            typeof update === 'function'
              ? update(state.deletedObjects)
              : update;
        }),

      initializeFiltersState: ({ currentView, uiMode, initialFilter }) =>
        set((state) => {
          const newFiltersState = currentView
            ? makeFiltersState({
                initialFilter,
                currentView,
                uiMode,
              })
            : getInitialFiltersStateForUIMode({
                initialFilter,
                uiMode,
              });

          const hasNoChanges = isEqual(state.filtersState, newFiltersState);
          if (!hasNoChanges) {
            state.filtersState = newFiltersState;
            state.filterStateSource = 'other';
          }
        }),
      updateInitialFilters: ({ initialFilter, uiMode }) =>
        set((state) => {
          const newFiltersState = combineFilterStates({
            initialFilter,
            newFilter: state.filtersState,
            uiMode,
          });
          const hasNoChanges = isEqual(state.filtersState, newFiltersState);
          if (!hasNoChanges) {
            state.filtersState = newFiltersState;
            state.filterStateSource = 'other';
          }
        }),
      consumeQueryParams: ({ currentView, urlParams, uiMode, initialFilter }) =>
        set((state) => {
          const newFilter = currentView
            ? makeFiltersState({
                initialFilter,
                currentView: {
                  ...currentView,
                  filterGroups: combineFilters(
                    currentView.filterGroups,
                    urlParams.filterGroups
                  ),
                  filters: [...currentView.filters, ...urlParams.filters],
                  quickFilters: [
                    ...currentView.quickFilters,
                    ...urlParams.quickFilters,
                  ],
                },
                uiMode,
              })
            : {
                ...state.filtersState,
                filterGroups: combineFilters(
                  state.filtersState.filterGroups,
                  urlParams.filterGroups
                ),
                quickFilters: [
                  ...state.filtersState.quickFilters,
                  ...urlParams.quickFilters,
                ],
              };

          state.hasUsedQueryParams = true;
          if (!isEqual(state.filtersState, newFilter)) {
            state.filtersState = newFilter;
            state.filterStateSource = 'other';
          }
        }),

      setFilters: (update, { source } = { source: 'other' }) =>
        set((state) => {
          const newFilters = resolveFiltersUpdate(update, state.filtersState);
          if (isEqual(state.filtersState, newFilters)) {
            return;
          }
          state.filtersState = newFilters;
          state.filterStateSource = source;
          state.shouldSyncWithSessionStorage = true;
        }),
    }),
    {
      devtools: { name: 'FilterStateStore' },
    }
  );
}

export const filtersStateSelectors = {
  selectHasPropertyFilter: (
    state: FilterSlice,
    { propertyName }: { propertyName: string }
  ) =>
    state.filtersState.quickFilters.some(
      (filterOperator) => filterOperator.property === propertyName
    ) ||
    state.filtersState.filterGroups.some((filterGroup) =>
      filterGroup.filters.some(
        (filterOperator) => filterOperator.property === propertyName
      )
    ),
};
